<script setup lang="ts">
import { DgrIcon, DgrPopover } from '@stockmarkteam/donguri-ui';
import { AdpDocumentOptionAction, AdpDocumentOptionActionName } from '@/types';

interface Props {
  optionActions: AdpDocumentOptionAction[];
  disabled?: boolean;
  size?: 's' | 'm';
  zindex?: number;
}

withDefaults(defineProps<Props>(), {
  disabled: false,
  size: 'm',
  zindex: 2,
});

const emit = defineEmits<{
  optionAction: [emitName: AdpDocumentOptionActionName];
}>();
</script>

<template>
  <DgrPopover :z-index="zindex">
    <template #default="{ triggerProps }">
      <button
        class="m-menu-button"
        :class="{ small: size === 's' }"
        :disabled="disabled"
        @click.stop
        v-bind="triggerProps"
      >
        <DgrIcon
          size="small"
          name="ellipsis-h"
          alt="option icon"
          :class="{ disabled: disabled }"
          :keep-fill="false"
        />
      </button>
    </template>
    <template #content="{ close }">
      <div class="m-menu-list">
        <ul>
          <li
            v-for="option in optionActions"
            :key="option.label"
            class="m-item-button"
            :class="
              option.subDescription ? 'm-item-button-with-sub-description' : ''
            "
            role="button"
            tabindex="0"
            @click="
              emit('optionAction', option.emitName);
              close();
            "
            @keydown="
              if ($event.key === 'Enter' || $event.key === ' ') {
                emit('optionAction', option.emitName);
                close();
              }
            "
            @keyup.space.prevent
          >
            <div>
              <DgrIcon size="small" :name="option.icon" class="m-icon" />
            </div>
            <div class="m-item-text">
              <span class="c-text-regular--m">{{ option.label }}</span>
              <div
                class="m-item-explanation c-text-regular--xs"
                v-if="option.subDescription"
              >
                {{ option.subDescription }}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </DgrPopover>
</template>

<style lang="scss" scoped>
.m-menu-button {
  height: auto;
  padding: 8px;
  border: none;
  border-radius: 4px;
  background-color: white;
  &.small {
    padding: 4px;
  }
  &:not([disabled]):hover {
    background-color: $color-gray200;
  }
  &[disabled] {
    cursor: auto;
  }
}

.disabled {
  cursor: auto;
  color: $color-gray600;
}

.m-item-button {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: auto;
  border: none;
  padding: 4px 8px;
  background-color: white;
  text-align: start;

  &:hover {
    background-color: $color-gray200;
  }
}

.m-icon {
  flex-shrink: 0;
}

.m-item-text {
  display: flex;
  flex-direction: column;
}

.m-item-button-with-sub-description {
  align-items: flex-start;

  .m-icon {
    margin-top: 5px;
  }
}

ul {
  list-style: none;

  li {
    cursor: pointer;
    flex-shrink: 0;
  }
}

span {
  margin-left: 6px;
  color: $color-gray1000;
}

.m-menu-list {
  display: flex;
  flex-direction: column;
  width: max-content;
  padding: 12px 0;

  .m-item-explanation {
    color: $color-gray800;
    margin-left: 6px;
  }
}
</style>
